"use client";

import { addLeadingSlash } from "@trydig/utils";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import type { ComponentProps } from "react";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { routing, useGeo } from "@/i18n/routing";
import { humanizeString } from "@/lib/utils/humanizeString";

type Size = ComponentProps<typeof Breadcrumbs>["size"];

interface PathBreadcrumbsProps {
	/** Override label for the last (current-page) segment. */
	title?: string;
	size?: Size;
	className?: string;
}

/**
 * Derives breadcrumb items from the current URL slug params (`/products/...`,
 * `/about/our-stores`, …), strips locale/group prefixes, and humanizes each
 * segment. Always prepends a "Home" link. Last segment uses `title` when
 * provided, otherwise the humanized slug.
 */
export const PathBreadcrumbs = ({ title, size, className }: PathBreadcrumbsProps) => {
	const { slug } = useParams<{ slug?: string[] }>();
	const { country, language } = useGeo();
	const homeHref = `/${language}-${country}`;
	const segments = (slug ?? []).filter((s) => !routing.groups.includes(s));

	const trail = segments.map((segment, index, arr) => {
		const href = addLeadingSlash(arr.slice(0, index + 1).join("/"));
		const isLast = index === arr.length - 1;
		const label = isLast && title ? title : humanizeString(segment);
		return isLast ? { label } : { label, href };
	});

	const home = trail.length === 0 ? { label: "Home" } : { label: "Home", href: homeHref };
	const items = [home, ...trail];

	// Mirror the PageHeader rule: a single crumb (just "Home") adds no
	// navigational value, so render nothing.
	if (items.length <= 1) return null;

	return <Breadcrumbs items={items} size={size} linkAs={NextLink} className={className} />;
};
